import React, { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Tooltip,
  TableCell,
  TableRow,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Collapse,
  Box,
  Table,
  TableHead,
  TableBody,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PreviewIcon from "@mui/icons-material/Preview";
import PauseIcon from "@mui/icons-material/Pause";
import CloseIcon from "@mui/icons-material/Close";
import ArchiveIcon from "@mui/icons-material/Archive";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import DescriptionIcon from "@mui/icons-material/Description";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { jobCampaignsService } from "../../Services/jobCampaigns.service";
import ModelConfirmPublishCampaigns from "../../Components/ModelConfirmPublishCampaigns";
import AddInviteInfluencer from "./AddInviteInfluencer";
import {
  decodeFromBase64,
  parseInput,
} from "../../Components/Constants/AppConstant";
import { Key } from "lucide-react";
import OfferItemByCampaignId from "./OfferItemByCampaignId";

const statusOptions = [
  { id: "all", value: "All Status" },
  { id: "p", value: "Pending" },
  { id: "c", value: "Complete" },
];

// Shimmer row component for loading state
const TableRowShimmer = ({ index }) => (
  <TableRow
    sx={{
      "& td": {
        py: 1,
        px: 2.5,
        borderBottom: "1px solid #F3F4F6",
      },
      backgroundColor:
        index % 2 === 0 ? "rgba(248, 250, 252, 0.5)" : "transparent",
    }}
  >
    <TableCell sx={{ width: "40px", minWidth: "40px" }}>
      <div className="w-6 h-5 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "100px" }}>
      <div className="h-4 w-20 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "150px" }}>
      <div className="h-4 w-3/4 rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "200px" }}>
      <div className="h-4 w-full rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "100px" }}>
      <div className="h-4 w-16 mx-auto rounded bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ textAlign: "center", minWidth: "120px" }}>
      <div className="h-6 w-24 mx-auto rounded-full bg-gray-200 animate-pulse" />
    </TableCell>
    <TableCell sx={{ minWidth: "150px" }}>
      <div className="h-8 w-20 rounded bg-gray-200 animate-pulse" />
    </TableCell>
  </TableRow>
);

export default function JobCampaignsTable(props) {
  console.log("🚀 ~ JobCampaignsTable ~ props:", props);
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [showPauseModal, setShowPauseModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const open = Boolean(anchorEl);
  const [offerDetails, setOfferDetails] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState(statusOptions[0]);
  const [toastList, setToastList] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [searchString, setSearchString] = useState("");
  const [hasMore, setHasMore] = useState(true);
  const [showLimitedRows, setShowLimitedRows] = useState(true);
  const [displayLimit] = useState(4);
  const [allDataLoaded, setAllDataLoaded] = useState(false);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;

    // Trigger load when user is near bottom (50px threshold)
    if (
      scrollHeight - scrollTop <= clientHeight + 50 &&
      !isFetching &&
      hasMore &&
      !showLimitedRows &&
      !allDataLoaded
    ) {
      loadAllData();
    }
  };

  const loadAllData = async () => {
    if (allDataLoaded || isFetching) return;

    try {
      setIsFetching(true);

      // Start from the next page
      const nextPage = page + 1;

      let filters = [];
      if (selectedStatus.id && selectedStatus.id !== "all") {
        filters.push(`status=${selectedStatus.id}`);
      }
      if (searchString.trim()) {
        filters.push(`inf=${encodeURIComponent(searchString.trim())}`);
      }
      // const filter = filters.length ? filters.join("&") : "";

      // const resApi = await offerService.getAll({
      //   page: nextPage,
      //   pageSize,
      //   filter,
      // });

      const resApi = await jobCampaignsService.getOffersByCampaignId(props.id);

      const res = resApi.data;

      if (res?.data && res.data.length > 0) {
        const totalPages =
          res.last_page || Math.ceil((res.total || 0) / pageSize);
        const currentPage = res.current_page || nextPage;

        setPaginationData({
          per_page: res.per_page || pageSize,
          total: res.total || 0,
          current_page: currentPage,
          total_pages: totalPages,
        });

        // Append new data
        setOfferDetails((prev) => {
          const existingIds = new Set(prev.map((item) => item.id));
          const uniqueNewData = res.data.filter(
            (item) => !existingIds.has(item.id)
          );
          return [...prev, ...uniqueNewData];
        });

        // Update page number
        setPage(nextPage);

        // Check if there's more data
        if (currentPage >= totalPages) {
          setAllDataLoaded(true);
          setHasMore(false);
        } else {
          setHasMore(true);
        }
      } else {
        setAllDataLoaded(true);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error loading data:", err);
      setAllDataLoaded(true);
      setHasMore(false);
    } finally {
      setIsFetching(false);
    }
  };

  const handleMenuClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = (event) => {
    if (event) event.stopPropagation();
    setAnchorEl(null);
  };

  const handlePauseCampaign = (event) => {
    event.stopPropagation();
    setShowPauseModal(true);
    handleMenuClose();
  };

  const handleEdit = (event) => {
    event.stopPropagation();
    if (props.onEdit) {
      props.onEdit();
    } else {
      console.log("Edit campaign:", props.id);
    }
    handleMenuClose();
  };

  const handleView = (event) => {
    event.stopPropagation();
    props.onClick();
    handleMenuClose();
  };

  const handleApplicationView = (event) => {
    event.stopPropagation();
    props.onApplicationCountClick(props.id);
  };

  const handleStatusChange = async (event, newStatus) => {
    event.stopPropagation();
    try {
      await jobCampaignsService.updateCampaignStatus(props.id, {
        status: newStatus,
      });
      if (props.onRefresh) {
        props.onRefresh();
      }
    } catch (error) {
      console.error("Error updating campaign status:", error);
    }
    handleMenuClose();
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    setShowDeleteDialog(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = (event) => {
    if (event) event.stopPropagation();
    setShowDeleteDialog(false);
    if (props.onDelete) {
      props.onDelete(props.id);
    } else {
      console.log("Delete campaign:", props.id);
    }
  };

  const handleDeleteCancel = (event) => {
    if (event) event.stopPropagation();
    setShowDeleteDialog(false);
  };

  // New function to handle publish campaign
  const handlePublishCampaign = (event) => {
    event.stopPropagation();
    setShowPublishModal(true);
    handleMenuClose();
  };

  const handleInviteInfluencer = (event) => {
    event.stopPropagation();
    setShowInviteModal(true);
    handleMenuClose();
  };
  const addToast = (data) => {
    const newToastList = [...props.toastList];
    newToastList.push({
      ...data,
      id: `${Date.now()}-${props.toastList.length}`,
    });
    props.setToastList(newToastList);
  };

  const handlePauseConfirm = async () => {
    try {
      const res = await jobCampaignsService.updateCampaignStatus(props.id, {
        status: "paused",
      });
      const decodedResponse =
        typeof res === "string" ? decodeFromBase64(res) : res;

      if (decodedResponse.success === false) {
        addToast({
          error: true,
          msg: decodedResponse.message || "Failed to pause campaign",
          id: `${Date.now()}-${props.toastList.length}`,
        });
      } else {
        addToast({
          error: false,
          msg: decodedResponse.message || "Campaign paused successfully!",
          id: `${Date.now()}-${props.toastList.length}`,
        });

        if (props.onRefresh) {
          props.onRefresh();
        }
      }
    } catch (err) {
      console.error("Error pausing campaign:", err);
      let errorMessage = "Failed to pause campaign";

      if (err.response?.data) {
        const decodedError = decodeFromBase64(err.response.data);
        errorMessage = decodedError.message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }

      addToast({
        error: true,
        msg: errorMessage,
        id: `${Date.now()}-${props.toastList.length}`,
      });
    } finally {
      setShowPauseModal(false);
    }
  };

  // New function to handle publish confirmation
  const handlePublishConfirm = async () => {
    try {
      const res = await jobCampaignsService.updateCampaignStatus(props.id, {
        status: "open",
      });
      const decodedResponse =
        typeof res === "string" ? decodeFromBase64(res) : res;

      if (decodedResponse.success === false) {
        addToast({
          error: true,
          msg: decodedResponse.message || "Failed to publish campaign",
          id: `${Date.now()}-${props.toastList.length}`,
        });
      } else {
        addToast({
          error: false,
          msg: decodedResponse.message || "Campaign published successfully!",
          id: `${Date.now()}-${props.toastList.length}`,
        });

        if (props.onRefresh) {
          props.onRefresh();
        }
      }
    } catch (err) {
      console.error("Error publishing campaign:", err);
      let errorMessage = "Failed to publish campaign";

      if (err.response?.data) {
        const decodedError = decodeFromBase64(err.response.data);
        errorMessage = decodedError.message || errorMessage;
      } else if (err.message) {
        errorMessage = err.message;
      }

      addToast({
        error: true,
        msg: errorMessage,
        id: `${Date.now()}-${props.toastList.length}`,
      });
    } finally {
      setShowPublishModal(false);
    }
  };

  const getAvailableActions = () => {
    const status = (
      props.status === "" ? "paused" : props.status
    )?.toLowerCase();
    const actions = [];

    actions.push({
      key: "view",
      label: "View Details",
      icon: VisibilityIcon,
      color: "#2563eb",
      hoverColor: "#dbeafe",
    });
    {
      props.application_count > 0 &&
        actions.push({
          key: "applicationView",
          label: "View Applications",
          icon: PreviewIcon,
          color: "#be185d",
          hoverColor: "#f3e8ff",
        });
    }
    if (status !== "archived") {
      actions.push({
        key: "edit",
        label: "Edit Campaign",
        icon: EditIcon,
        color: status === "open" ? "#9ca3af" : "#16a34a",
        hoverColor: status === "open" ? "#f9fafb" : "#dcfce7",
        disabled: status === "open",
        tooltip:
          status === "open"
            ? "Campaign cannot be edited after publishing."
            : "",
      });
    }

    switch (status) {
      case "open":
        actions.push({
          key: "closed",
          label: "Close Campaign",
          icon: CloseIcon,
          color: "#ea580c",
          hoverColor: "#fed7aa",
        });
        actions.push({
          key: "paused",
          label: "Pause Campaign",
          icon: PauseIcon,
          color: "#9333ea",
          hoverColor: "#f3e8ff",
          onClick: handlePauseCampaign,
        });
        break;
      case "close":
      case "closed":
        actions.push({
          key: "archive",
          label: "Archive Campaign",
          icon: ArchiveIcon,
          color: "#6b7280",
          hoverColor: "#f3f4f6",
        });
        break;
      case "pause":
      case "paused":
        actions.push({
          key: "open",
          label: "Open Campaign",
          icon: PlayArrowIcon,
          color: "#16a34a",
          hoverColor: "#dcfce7",
        });
        actions.push({
          key: "closed",
          label: "Close Campaign",
          icon: CloseIcon,
          color: "#ea580c",
          hoverColor: "#fed7aa",
        });
        break;
      case "draft":
        // Modified this action to use the new handlePublishCampaign function
        actions.push({
          key: "publish",
          label: "Publish Campaign",
          icon: PlayArrowIcon,
          color: "#16a34a",
          hoverColor: "#dcfce7",
          onClick: handlePublishCampaign, // Add onClick handler
        });
        actions.push({
          key: "delete",
          label: "Delete Campaign",
          icon: DeleteIcon,
          color: "#dc2626",
          hoverColor: "#fecaca",
        });
        break;

      default:
        break;
    }
    if (status === "open" && props.cmp_type === "r") {
      actions.push({
        key: "invite",
        label: "Invite Influencer",
        icon: PersonAddIcon,
        color: "#0891b2",
        hoverColor: "#cffafe",
      });
    }

    if (props.has_report) {
      actions.push({
        key: "viewReports",
        label: "View Reports",
        icon: DescriptionIcon,
        color: "#0891b2",
        hoverColor: "#cffafe",
      });
    }

    return actions;
  };

  const renderCellWithTooltip = (content, maxLength = 35) => {
    if (!content) return null;

    const truncated =
      content.length > maxLength
        ? `${content.substring(0, maxLength)}...`
        : content;

    return (
      <Tooltip title={content} arrow placement="top">
        <span className="truncate">{truncated}</span>
      </Tooltip>
    );
  };

  const renderCellApplicationCountWithTooltip = (applicationCount) => {
    return (
      <Tooltip title={`View Applications`} arrow placement="top">
        <span className="truncate">{applicationCount}</span>
      </Tooltip>
    );
  };

  const getStatusColor = () => {
    const status = (
      props.status === "" ? "paused" : props.status
    )?.toLowerCase();
    switch (status) {
      case "draft":
        return "warning";
      case "pending":
        return "info";
      case "open":
      case "active":
        return "success";
      case "pause":
      case "paused":
        return "default";
      case "delete":
      case "deleted":
      case "archive":
      case "archived":
      case "close":
      case "closed":
        return "error";
      default:
        return "default";
    }
  };

  const safeOfferDetails = Array.isArray(offerDetails) ? offerDetails : [];

  const getOfferData = async () => {
    try {
      if (page === 1) {
        setIsLoaded(false);
      } else {
        setIsLoadingMore(true);
      }

      setIsFetching(true);

      let filters = [];

      if (selectedStatus.id && selectedStatus.id !== "all") {
        filters.push(`status=${selectedStatus.id}`);
      }

      if (searchString.trim()) {
        filters.push(`inf=${encodeURIComponent(searchString.trim())}`);
      }

      const filter = filters.length ? filters.join("&") : "";

      console.log("🔍 Fetching offers - page:", page, "filter:", filter);

      // const resApi = await offerService.getAll({
      //   page,
      //   pageSize,
      //   filter,
      // });
      console.log("🚀 ~ getOfferData ~ props:", props);

      const resApi = await jobCampaignsService.getOffersByCampaignId(props.id);

      const res = resApi.data;

      if (res?.data) {
        const totalPages =
          res.last_page || Math.ceil((res.total || 0) / pageSize);
        const currentPage = res.current_page || page;

        setPaginationData({
          per_page: res.per_page || pageSize,
          total: res.total || 0,
          current_page: currentPage,
          total_pages: totalPages,
        });

        const newData = res.data || [];

        if (page === 1) {
          setOfferDetails(newData);
        } else {
          setOfferDetails((prev) => {
            const existingIds = new Set(prev.map((item) => item.id));
            const uniqueNewData = newData.filter(
              (item) => !existingIds.has(item.id)
            );
            return [...prev, ...uniqueNewData];
          });
        }

        setHasMore(currentPage < totalPages);
      } else {
        setOfferDetails([]);
        setHasMore(false);
      }

      setIsLoaded(true);
    } catch (err) {
      console.error("❌ Error fetching offers:", err);

      if (err.apiResponseStatus !== 404) {
        let error = parseInput(err?.message);
        addToast({
          error: true,
          msg: error?.message || "Failed to load offers",
          id: `${Date.now()}-${toastList.length}`,
        });
      } else {
        setIsLoaded(true);
      }

      setHasMore(false);
    } finally {
      setIsFetching(false);
      setIsLoadingMore(false);
    }
  };

  const refreshOfferData = useCallback(() => {
    setPage(1);
    setOfferDetails([]);
    setHasMore(true);
  }, []);

  return (
    <>
      <TableRow
        hover
        sx={{
          "& td": {
            py: 1.5,
            px: 2.5,
            borderBottom: "1px solid #F3F4F6",
            fontSize: "14px",
          },
          "&:hover": {
            backgroundColor: "#FAFAFA !important",
          },
          "&:nth-of-type(even)": {
            backgroundColor: "rgba(248, 250, 252, 0.5)",
          },
          cursor: "pointer",
        }}
      >
        <TableCell
          sx={{
            width: { xs: "40px", lg: "40px" },
            minWidth: "40px",
            paddingLeft: "12px",
          }}
        >
          {" "}
          <Tooltip title="Campaign Offers" arrow placement="top">
            <div className="flex items-center gap-1">
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  if (!isExpanded) {
                    getOfferData();
                    setShowLimitedRows(true);
                    setAllDataLoaded(false);
                  }
                  setIsExpanded(!isExpanded);
                }}
                sx={{
                  transition: "transform 0.3s ease",
                  transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <KeyboardArrowDownIcon sx={{ color: "#6B7280" }} />
              </IconButton>
              {props.status === "open" && props.cmp_type === "r" && (
                <Key className="w-5 h-4 text-amber-500" strokeWidth={2} />
              )}
            </div>
          </Tooltip>
        </TableCell>

        <TableCell
          sx={{
            color: "#1F2937",
            fontWeight: 500,
            fontSize: "15px",
            width: { xs: "180px", lg: "auto" },
            minWidth: "150px",
          }}
        >
          <span
            className="cursor-pointer"
            style={{
              display: "inline-block",
              maxWidth: "100%",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            onClick={(e) => {
              e.stopPropagation();
              props.onClick();
            }}
          >
            {renderCellWithTooltip(props.title)}
          </span>
        </TableCell>
        <TableCell
          sx={{
            textAlign: "center",
            color: "#4b5563",
            width: { xs: "100px", lg: "auto" },
            minWidth: "90px",
          }}
        >
          {props.application_count > 0 ? (
            <span
              className="cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                if (props.onApplicationCountClick) {
                  props.onApplicationCountClick(props.id);
                } else {
                  console.log(
                    "Navigate to applications for campaign:",
                    props.id
                  );
                }
              }}
            >
              {renderCellApplicationCountWithTooltip(props.application_count)}
            </span>
          ) : (
            <span className="text-gray-400">
              {renderCellApplicationCountWithTooltip(props.application_count)}
            </span>
          )}
        </TableCell>
        <TableCell
          sx={{
            textAlign: "center",
            color: "#64748B",
            fontSize: "14px",
            fontWeight: 400,
            width: { xs: "100px", lg: "auto" },
            minWidth: "90px",
          }}
        >
          {props.apply_by_date}
        </TableCell>
        <TableCell
          sx={{
            textAlign: "center",
            color: "#64748B",
            fontSize: "14px",
            fontWeight: 400,
            width: { xs: "100px", lg: "auto" },
            minWidth: "90px",
          }}
        >
          {props.delivery_date}
        </TableCell>
        <TableCell
          sx={{
            color: "#475569",
            fontSize: "14px",
            fontWeight: 400,
            width: { xs: "120px", lg: "auto" },
            minWidth: "100px",
          }}
        >
          {props.category_name}
        </TableCell>
        <TableCell
          sx={{
            textAlign: "center",
            width: { xs: "150px", lg: "auto" },
            minWidth: "150px",
          }}
        >
          <Chip
            label={props.status === "" ? "paused" : props.status}
            color={getStatusColor()}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: "12px",
              textTransform: "capitalize",
              borderRadius: "6px",
              height: "26px",
              whiteSpace: "nowrap",
              overflow: "visible",
              "& .MuiChip-label": {
                overflow: "visible",
                textOverflow: "clip",
                whiteSpace: "nowrap",
              },
            }}
          />
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-center">
            <IconButton
              onClick={handleMenuClick}
              className="text-gray-600 hover:text-blue-600 transition-colors"
              size="small"
            >
              <MoreVertIcon />
            </IconButton>

            <Menu
              anchorEl={anchorEl}
              open={open}
              onClose={handleMenuClose}
              PaperProps={{
                className: "shadow-lg border border-gray-200",
                style: { minWidth: "180px" },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              {getAvailableActions().map((action, index) => {
                const menuItem = (
                  <MenuItem
                    key={action.key}
                    disabled={action.disabled}
                    onClick={(event) => {
                      if (action.disabled) return;
                      if (action.key === "view") {
                        handleView(event);
                      } else if (action.key === "applicationView") {
                        handleApplicationView(event);
                      } else if (action.key === "edit") {
                        handleEdit(event);
                      } else if (action.key === "delete") {
                        handleDelete(event);
                      } else if (action.key === "invite") {
                        handleInviteInfluencer(event);
                      } else if (action.key === "viewReports") {
                        navigate(
                          `/brand/reports/insights/${props.id}?source=campaign`
                        );
                        handleMenuClose();
                      } else if (action.onClick) {
                        action.onClick(event);
                      } else {
                        handleStatusChange(event, action.key);
                      }
                    }}
                    sx={{
                      "&:hover": {
                        backgroundColor: action.disabled
                          ? "transparent"
                          : action.hoverColor,
                      },
                    }}
                  >
                    <ListItemIcon>
                      <action.icon
                        sx={{ color: action.color }}
                        fontSize="small"
                      />
                    </ListItemIcon>
                    <ListItemText primary={action.label} />
                  </MenuItem>
                );

                return action.tooltip ? (
                  <Tooltip
                    key={action.key}
                    title={action.tooltip}
                    arrow
                    placement="top"
                  >
                    <span>{menuItem}</span>
                  </Tooltip>
                ) : (
                  menuItem
                );
              })}
            </Menu>
          </div>
        </TableCell>
      </TableRow>

      {/* Collapsible Row with Nested Table */}
      <TableRow>
        <TableCell
          style={{
            paddingBottom: 0,
            paddingTop: 0,
            backgroundColor: "#FAFBFC",
          }}
          colSpan={8}
        >
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box
              sx={{ marginX: 1.5, marginY: "2px" }}
              style={{
                overflowY: showLimitedRows ? "visible" : "auto",
                overflowX: "auto",
                maxHeight: showLimitedRows ? "none" : "400px",
                scrollbarWidth: "thin",
                scrollbarColor: "#e5e7eb transparent",
              }}
              onScroll={handleScroll}
            >
              {!isLoaded && safeOfferDetails.length === 0 ? (
                <Table size="small" sx={{ minWidth: "100%" }}>
                  <TableHead
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 10,
                      background:
                        "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                      "& th": {
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        borderBottom: "2px solid #E2E8F0",
                      },
                    }}
                  >
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
                        "& th": {
                          fontWeight: 700,
                          fontSize: "11px",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          borderBottom: "2px solid #CBD5E1",
                          py: "4px",
                          px: 2,
                        },
                      }}
                    >
                      <TableCell sx={{ width: "40px" }}></TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Influencer</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        Offered Amt
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>Status</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {[...Array(displayLimit)].map((_, index) => (
                      <TableRowShimmer key={`shimmer-${index}`} index={index} />
                    ))}
                  </TableBody>
                </Table>
              ) : safeOfferDetails.length > 0 ? (
                <Table size="small" sx={{ minWidth: "100%" }}>
                  <TableHead
                    sx={{
                      position: "sticky",
                      top: 0,           
                      background:
                        "linear-gradient(180deg, #F8FAFC 0%, #F1F5F9 100%)",
                      "& th": {
                        fontWeight: 600,
                        fontSize: "12px",
                        color: "#475569",
                        textTransform: "uppercase",
                        letterSpacing: "0.08em",
                        borderBottom: "2px solid #E2E8F0",
                      },
                    }}
                  >
                    <TableRow
                      sx={{
                        background:
                          "linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%)",
                        "& th": {
                          fontWeight: 700,
                          fontSize: "11px",
                          color: "#475569",
                          textTransform: "uppercase",
                          letterSpacing: "0.08em",
                          borderBottom: "2px solid #CBD5E1",
                          py: "4px",
                          px: 2,
                        },
                      }}
                    >
                      <TableCell sx={{ width: "40px" }}></TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Influencer</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>
                        Offered Amt
                      </TableCell>
                      <TableCell sx={{ textAlign: "center" }}>Status</TableCell>
                      <TableCell sx={{ textAlign: "center" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(showLimitedRows
                      ? safeOfferDetails.slice(0, displayLimit)
                      : safeOfferDetails
                    ).map((item, index) => (
                      <OfferItemByCampaignId
                        key={`${item.id}-${index}`}
                        user={props.user}
                        reFetchData={refreshOfferData}
                        onStartChat={props.onStartChat}
                        setOfferSelectedForReview={
                          props.setOfferSelectedForReview
                        }
                        {...item}
                      />
                    ))}

                    {/* Load More Button */}
                    {isLoaded &&
                      safeOfferDetails.length > displayLimit &&
                      showLimitedRows && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => setShowLimitedRows(false)}
                              sx={{
                                textTransform: "none",
                                borderRadius: "6px",
                                fontSize: "12px",
                                color: "#475569",

                                border: "none",
                                boxShadow: "none",
                                textDecoration: "underline",

                                "&:hover": {
                                  backgroundColor: "transparent",
                                  textDecoration: "underline",
                                },
                              }}
                            >
                              Load More ...
                            </Button>
                          </TableCell>
                        </TableRow>
                      )}

                    {/* Loading Indicator */}
                    {isLoaded &&
                      !showLimitedRows &&
                      isFetching &&
                      safeOfferDetails.length > 0 && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                            <div className="flex items-center justify-center gap-2">
                              <div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
                              <span className="text-sm text-gray-600">
                                Loading more...
                              </span>
                            </div>
                          </TableCell>
                        </TableRow>
                      )}

                    {/* All Loaded Message */}
                    {isLoaded &&
                      !showLimitedRows &&
                      allDataLoaded &&
                      safeOfferDetails.length > displayLimit && (
                        <TableRow>
                          <TableCell colSpan={7} align="center" sx={{ py: 2 }}>
                            <span className="text-sm text-gray-500">
                              All {safeOfferDetails.length} offers loaded
                            </span>
                          </TableCell>
                        </TableRow>
                      )}
                  </TableBody>
                </Table>
              ) : (
                <div
                  style={{
                    textAlign: "center",
                    padding: "8px 16px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "6px",
                    color: "#9CA3AF",
                    fontSize: "13px",
                    fontStyle: "italic",
                    backgroundColor: "#F9FAFB",
                    borderRadius: "6px",
                    margin: "4px 0",
                  }}
                >
                  <svg 
                    width="16" 
                    height="16" 
                    viewBox="0 0 24 24" 
                    fill="none" 
                    stroke="currentColor" 
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="12" r="10"/>
                    <line x1="12" y1="8" x2="12" y2="12"/>
                    <line x1="12" y1="16" x2="12.01" y2="16"/>
                  </svg>
                  No offers found
                </div>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      <Dialog
        open={showDeleteDialog}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          id="delete-dialog-title"
          className="text-red-600 font-semibold"
        >
          Confirm Delete Campaign
        </DialogTitle>
        <DialogContent>
          <DialogContentText
            id="delete-dialog-description"
            className="text-gray-700"
          >
            Are you sure you want to delete the campaign "
            <strong>{props.title}</strong>"? This action cannot be undone and
            will permanently remove all campaign data.
          </DialogContentText>
        </DialogContent>
        <DialogActions className="p-4 gap-2">
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            className="text-gray-600 border-gray-300 hover:bg-gray-50"
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            className="bg-red-600 hover:bg-red-700 text-white"
            autoFocus
          >
            Yes, Delete
          </Button>
        </DialogActions>
      </Dialog>

      {showPublishModal && (
        <ModelConfirmPublishCampaigns
          open={showPublishModal}
          onClose={() => setShowPublishModal(false)}
          onConfirm={handlePublishConfirm}
          campaignTitle={props.title}
        />
      )}

      {showInviteModal && (
        <AddInviteInfluencer
          isOpen={showInviteModal}
          close={() => setShowInviteModal(false)}
          {...props}
        />
      )}
      {showPauseModal && (
        <Dialog
          open={showPauseModal}
          onClose={() => setShowPauseModal(false)}
          aria-labelledby="pause-dialog-title"
          aria-describedby="pause-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            className: "rounded-xl",
          }}
        >
          <DialogTitle
            id="pause-dialog-title"
            className="text-emerald-700 font-semibold bg-gradient-to-r from-emerald-50 to-emerald-100 border-b border-emerald-200"
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              pb: 2,
            }}
          >
            Confirm Pause Campaign
          </DialogTitle>
          <DialogContent sx={{ mt: 2 }}>
            <DialogContentText
              id="pause-dialog-description"
              className="text-gray-700"
            >
              Are you sure you want to pause the campaign "
              <strong className="text-gray-900">{props.title}</strong>"?
              <br />
            </DialogContentText>
          </DialogContent>
          <DialogActions className="p-4 gap-2 bg-gray-50">
            <Button
              onClick={() => setShowPauseModal(false)}
              variant="outlined"
              className="!text-gray-600 !border-gray-300 hover:!bg-gray-100"
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handlePauseConfirm}
              variant="contained"
              className="bg-gradient-to-r from-green-500 to-green-700 hover:from-green-700 hover:to-green-500 text-white"
              autoFocus
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                px: 3,
              }}
            >
              Yes, Pause Campaign
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </>
  );
}
